import { currencyCodeToLocales } from '../../../app/data/currencies';
import {
  NumberFormatType,
  NumberFormatConfigs,
  NegativeNumFormatType,
} from '../../types';

export const createFormatCode = (configs: NumberFormatConfigs) => {
  switch (configs.type) {
    case NumberFormatType.Automatic:
      return '';
    case NumberFormatType.Percent:
      return percentFormat(configs.decimalPlaces);
    case NumberFormatType.Number:
      return numberFormat(configs);
    case NumberFormatType.Currency:
      return currencyFormat(configs);
    case NumberFormatType.Accounting:
      return accountingFormat(configs);
    case NumberFormatType.Scientific:
      return scientificFormat(configs.decimalPlaces);
    case NumberFormatType.DateNTime:
      return configs.dateFormat.length || configs.timeFormat.length
        ? (configs.dateFormat + ' ' + configs.timeFormat).trim()
        : 'YYYY-MM-DD';
    case NumberFormatType.Fraction:
      return configs.fractionFormat;
    case NumberFormatType.Duration:
      return configs.durationFormat;
    case NumberFormatType.Text:
      return '@';
    case NumberFormatType.Custom:
      return configs.customCode;
  }
};

const BASE_CODE = '###0';

const percentFormat = (decimalPlaces: string | number) => {
  let formatCode = addDecimalPlaces(BASE_CODE, decimalPlaces);
  return formatCode + '%';
};

const scientificFormat = (decimalPlaces: string | number) => {
  let formatCode = addDecimalPlaces('0', decimalPlaces);
  return formatCode + 'E+0';
};

const numberFormat = ({
  showThousandSeparator,
  decimalPlaces,
  negativeFormat,
}: NumberFormatConfigs) => {
  let formatCode = addThousandSeparator(BASE_CODE, showThousandSeparator);
  formatCode = addDecimalPlaces(formatCode, decimalPlaces);
  formatCode = addNegativeFormat(formatCode, negativeFormat);

  return formatCode;
};

const currencyFormat = ({
  currencyCode,
  showThousandSeparator,
  decimalPlaces,
  negativeFormat,
}: NumberFormatConfigs) => {
  let formatCode = addThousandSeparator(BASE_CODE, showThousandSeparator);
  formatCode = addDecimalPlaces(formatCode, decimalPlaces);
  formatCode = addCurrency(formatCode, currencyCode);
  formatCode = addNegativeFormat(formatCode, negativeFormat);
  return formatCode;
};

const accountingFormat = ({
  decimalPlaces,
  showThousandSeparator,
  currencyCode,
}: NumberFormatConfigs) => {
  // Sample: _(* #,##0.00_);_(* (#,##0.00);_(* "-"??_);_(@_)

  let formatCode = addThousandSeparator(BASE_CODE, showThousandSeparator);
  formatCode = addDecimalPlaces(formatCode, decimalPlaces);

  const currency = addCurrency('', currencyCode);

  const start = `_(${currency}*`;
  const positive = `${start} ${formatCode}_)`;
  const negative = `${start} (${formatCode})_)`; // Include brackets
  const zero = `${start} "-"??_)`;
  const text = `_(@_)`;

  return `${positive};${negative};${zero};${text}`;
};

// #helpers
const addDecimalPlaces = (code: string, decimalPlaces: string | number) => {
  const places = Number(decimalPlaces);
  if (places > 0) {
    code += '.' + '0'.repeat(places);
  }
  return code;
};

const addThousandSeparator = (code: string, thousandSeparator = false) => {
  if (thousandSeparator) {
    code = '#,##0';
  }
  return code;
};

const addNegativeFormat = (
  code: string,
  negativeFormat: NegativeNumFormatType
) => {
  if (negativeFormat === NegativeNumFormatType.StandardWithSign) return code;
  let negativeCode = '';

  switch (negativeFormat) {
    case NegativeNumFormatType.NegativeAsRed:
      negativeCode += `[RED]${code}`;
      break;
    case NegativeNumFormatType.NegativeAsRedInBrackets:
      negativeCode += `[RED](${code})`;
      break;
    case NegativeNumFormatType.NegativeAsRedWithSign:
      negativeCode += `[RED]-${code}`;
      break;
    case NegativeNumFormatType.NegativeInBrackets:
      negativeCode += `(${code})`;
      break;
  }

  return `${code};${negativeCode}`;
};

const addCurrency = (code: string, currencyCode: string) => {
  const locale = currencyCodeToLocales[currencyCode];
  if (!locale) return code;
  return `[$${locale}]${code}`;
};
// #endregion
